// ============================================
// INDIVIDUALIZED WEIGHT LOSS FOLLOW-UP QUESTIONNAIRE DATA
// ============================================

// Medication dose options
export const SEMAGLUTIDE_DOSES = [
  { label: 'Semaglutide 0.2mg', value: 'Semaglutide 0.2mg', isStarter: true },
  { label: 'Semaglutide 0.4mg', value: 'Semaglutide 0.4mg' },
  { label: 'Semaglutide 0.6mg', value: 'Semaglutide 0.6mg' },
  { label: 'Semaglutide 0.9mg', value: 'Semaglutide 0.9mg' },
  { label: 'Semaglutide 1.12mg', value: 'Semaglutide 1.12mg' },
  { label: 'Semaglutide 1.5mg', value: 'Semaglutide 1.5mg' },
  { label: 'Semaglutide 1.9mg', value: 'Semaglutide 1.9mg' },
  { label: 'Semaglutide 2.12mg', value: 'Semaglutide 2.12mg' },
  { label: 'Semaglutide 2.3mg', value: 'Semaglutide 2.3mg' },
];

export const TIRZEPATIDE_DOSES = [
  { label: 'Tirzepatide 1.5mg', value: 'Tirzepatide 1.5mg', isStarter: true },
  { label: 'Tirzepatide 3mg', value: 'Tirzepatide 3mg' },
  { label: 'Tirzepatide 6mg', value: 'Tirzepatide 6mg' },
  { label: 'Tirzepatide 9mg', value: 'Tirzepatide 9mg' },
  { label: 'Tirzepatide 11mg', value: 'Tirzepatide 11mg' },
  { label: 'Tirzepatide 13.5mg', value: 'Tirzepatide 13.5mg' },
];

// Starter doses for reference
export const STARTER_DOSES = {
  'Semaglutide': '0.2mg',
  'Tirzepatide': '1.5mg'
};

// Helper function to check if patient is on starter dose
export const isOnStarterDose = (patientPreference) => {
  if (!patientPreference || !Array.isArray(patientPreference) || patientPreference.length === 0) {
    return false;
  }
  
  const currentMed = patientPreference[0];
  if (!currentMed.name || !currentMed.strength) return false;
  
  const medName = currentMed.name.toLowerCase();
  const strength = currentMed.strength.toLowerCase();
  
  // Check if on Semaglutide starter dose (0.2mg)
  if (medName.includes('semaglutide') && strength.includes('0.2')) {
    return true;
  }
  
  // Check if on Tirzepatide starter dose (1.5mg)
  if (medName.includes('tirzepatide') && strength.includes('1.5')) {
    return true;
  }
  
  return false;
};

// Get starter dose for a medication type
export const getStarterDoseForMedication = (patientPreference) => {
  if (!patientPreference || !Array.isArray(patientPreference) || patientPreference.length === 0) {
    return null;
  }
  
  const currentMed = patientPreference[0];
  const medName = currentMed.name?.toLowerCase() || '';
  
  if (medName.includes('semaglutide')) {
    return { name: 'Semaglutide', strength: '0.2mg', medId: 'SEM-0.2' };
  }
  if (medName.includes('tirzepatide')) {
    return { name: 'Tirzepatide', strength: '1.5mg', medId: 'TIR-1.5' };
  }
  
  return null;
};

// ============================================
// FOLLOW-UP QUESTIONS
// ============================================

export const followUpQuestions = {
  // Pre-populated from user data (editable)
  medicalConditions: {
    id: 'medical-conditions',
    type: 'textarea-prefilled',
    question: 'Please identify all your current medical conditions',
    fieldMapping: 'medicalConditions',
    placeholder: 'e.g., Type 2 Diabetes, Hypertension, etc.',
    required: true
  },
  
  selfReportedMeds: {
    id: 'self-reported-meds',
    type: 'textarea-prefilled',
    question: 'Please list all your current medications including dosages',
    fieldMapping: 'selfReportedMeds',
    placeholder: 'e.g., Metformin 500mg twice daily, Lisinopril 10mg daily...',
    required: true
  },
  
  allergies: {
    id: 'allergies',
    type: 'textarea-prefilled',
    question: 'Please list all of your known allergies',
    fieldMapping: 'allergies',
    placeholder: 'e.g., Penicillin (causes rash), Shellfish (anaphylaxis)...',
    required: true
  },
  
  sexAtBirth: {
    id: 'sex-at-birth',
    type: 'single-select-prefilled',
    question: 'What was your sex assigned at birth?',
    fieldMapping: 'sex',
    options: [
      { label: 'Male', value: 'Male' },
      { label: 'Female', value: 'Female' }
    ],
    conditional: {
      showNext: 'pregnancy-status',
      when: 'Female'
    }
  },
  
  // Conditional: Only for Female
  pregnancyStatus: {
    id: 'pregnancy-status',
    type: 'single-select',
    question: 'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    showIf: { field: 'sex-at-birth', value: 'Female' },
    options: [
      { label: 'Yes', value: 'yes' },
      { 
        label: 'No and I acknowledge that these medications are not safe to use during pregnancy or while breastfeeding, as they may pose risks to a developing baby.', 
        value: 'no-acknowledge' 
      }
    ]
  },
  
  // Height and Weight for BMI calculation
  heightWeight: {
    id: 'height-weight',
    type: 'height-weight-bmi',
    question: 'What is your height and weight?',
    subtitle: 'We will calculate your BMI automatically'
  },
  
  // Health Changes
  healthChanges: {
    id: 'health-changes',
    type: 'single-select',
    question: 'Have there been any health changes, medication changes, ED visits, surgeries, or hospitalizations since your last check-in?',
    options: [
      { label: 'No', value: 'no' },
      { label: 'Yes', value: 'yes', triggersQuestion: 'health-changes-details' }
    ]
  },
  
  // Conditional: Health changes details
  healthChangesDetails: {
    id: 'health-changes-details',
    type: 'textarea',
    question: 'Please tell us more about the changes in your health',
    showIf: { field: 'health-changes', value: 'yes' },
    placeholder: 'Describe your health changes...',
    required: true
  },
  
  // Last Medication Date
  lastMedicationDate: {
    id: 'last-medication-date',
    type: 'single-select',
    question: 'When did you last take your prescribed weight loss medication?',
    options: [
      { label: 'Within the past week', value: 'within-past-week' },
      { label: 'Within the past 2-3 weeks', value: 'within-2-3-weeks', triggersQuestion: 'dose-decrease' },
      { label: 'It has been over a month', value: 'over-a-month', triggersQuestion: 'restart-medication' }
    ]
  },
  
  // Conditional: Dose Decrease Consent
  doseDecrease: {
    id: 'dose-decrease',
    type: 'consent-dropdown',
    question: 'Dose Reduction Notice',
    showIf: { field: 'last-medication-date', value: 'within-2-3-weeks' },
    consentText: `I'm so sorry, but these medications gradually build up in your system. Since you've been off this medication for an extended period, it's safest to restart at a lower dose, typically about 50% of your previous dose, and titrate back up. Restarting at a high dose after a break can cause significant side effects, sometimes requiring emergency care or hospitalization. Would you like to continue with a lower starting dose?`,
    options: [
      { label: 'I understand and agree to the dose reduction', value: 'consent' },
      { label: 'I do not agree and do not wish to continue', value: 'decline', disqualifies: true }
    ]
  },
  
  // Conditional: Restart Medication Consent
  restartMedication: {
    id: 'restart-medication',
    type: 'consent-dropdown',
    question: 'Restart Medication Notice',
    showIf: { field: 'last-medication-date', value: 'over-a-month' },
    consentText: `I'm so sorry, but these medications gradually build up in your system. Since you've been off this medication for an extended period, it's safest to restart at the starter dose and titrate back up. Restarting at a high dose after a break can cause significant side effects, sometimes requiring emergency care or hospitalization. Would you like to continue with a lower starting dose?`,
    options: [
      { label: 'I understand and agree to the dose reduction', value: 'consent', skipToQuestion: 'doctor-questions' },
      { label: 'I do not agree and do not wish to continue', value: 'decline', disqualifies: true }
    ]
  },
  
  // Last use date - shows for dose decrease consent OR within past week
  lastUseDate: {
    id: 'last-use-date',
    type: 'text',
    question: 'Please provide the approximate date (month and day) of your last use of this medication',
    placeholder: 'e.g., January 15',
    required: true,
    // Only skip if restart medication consent was given (they skip to doctor questions)
    skipIfRestartConsent: true
  },
  
  // Current Dose - shows for dose decrease consent OR within past week
  currentDose: {
    id: 'current-dose',
    type: 'single-select',
    question: 'Which medication and dose most closely matches your most recent dose?',
    subtitle: 'Keep in mind: Tirzepatide = Mounjaro, Zepbound | Semaglutide = Ozempic, Wegovy',
    // Only skip if restart medication consent was given
    skipIfRestartConsent: true,
    options: [
      ...SEMAGLUTIDE_DOSES,
      ...TIRZEPATIDE_DOSES
    ]
  },
  
  // Medication Details — collapsed into a single textarea slide whose Q
  // includes the full Beluga Safety preamble in subtitle. Prior version
  // emitted two Q/A pairs (info-text "Acknowledged" + textarea free-text)
  // which Erika flagged on visit 66402450 as "Acknowledged in answer field"
  // + redundant "Please provide your medication details" Q. Single-slide
  // model matches the intake compounded-GLP-1 medication-details slide
  // (`intakeData.js:454`) so Beluga sees one Q with the Safety preamble +
  // one A with the patient's free-text.
  medicationDetails: {
    id: 'medication-details',
    type: 'textarea-with-upload',
    question: 'Please tell us the name and dose of the weight loss medication you are taking',
    subtitle: `Safety is our top priority and to ensure that your provider can verify your current dose accurately, please share the information below as clearly as possible. Providing the exact details helps us confirm that your treatment plan is correct and safe. Here's how to share the information clearly:

1. Medication name – write the exact name from your prescription label including any additives such as glycine, vitamin B, etc.
2. Concentration – look on your prescription label or vial for something that looks like "20 mg/mL" or "2.5 mg/mL." There may be several numbers which should all be provided. This shows how strong your medication is. Please provide all the numbers listed.
3. Amount you took – write how much you injected or used, in units or milliliters (mL).
(Example: "Medication: Semaglutide. Concentration: 2.5 mg/mL. Dose taken: 0.5 mL.")
4. If you're unsure about your prescription details, please upload a clear photo of your medication label. Make sure the entire label is visible, and let us know how many units or milliliters (mL) you are currently using. You can upload the photo directly in your message thread.`,
    skipIfRestartConsent: true,
    placeholder: 'e.g., Medication: Semaglutide. Concentration: 2.5 mg/mL. Dose taken: 0.5 mL.',
    required: true
  },
  
  // Side Effects
  sideEffects: {
    id: 'side-effects',
    type: 'single-select',
    question: 'Have you experienced side effects from your current medication?',
    skipIfRestartConsent: true,
    options: [
      { label: 'Yes', value: 'yes', triggersQuestion: 'side-effects-details' },
      { label: 'No', value: 'no' }
    ]
  },
  
  // Conditional: Side Effects Details
  sideEffectsDetails: {
    id: 'side-effects-details',
    type: 'textarea',
    question: 'Please describe the side effects that you\'ve experienced',
    showIf: { field: 'side-effects', value: 'yes' },
    placeholder: 'Describe your side effects...',
    required: true
  },
  
  // Side Effects with Additives - matches normal GLP-1 flow
  sideEffectsAdditives: {
    id: 'side-effects-additives',
    type: 'multi-select-with-addons',
    question: 'Have you experienced any of the following side effects from your current medication?',
    subtitle: 'Select all that apply',
    showIf: { field: 'side-effects', value: 'yes' },
    options: [
      { 
        label: 'Yes, I have experienced fatigue and WOULD like to explore the option of including an additive which may help manage my fatigue symptoms',
        value: 'fatigue',
        hasOptionalAdditive: true,
        additiveLabel: 'Optional: Select an additive to help with fatigue',
        additives: [
          { label: 'B12 (Vitamin B12)', value: 'b12' },
          { label: 'B3 (Niacin)', value: 'b3' },
          { label: 'NAD+ (Nicotinamide Adenine Dinucleotide)', value: 'nad-plus' },
          { label: 'No additive', value: 'none' }
        ]
      },
      { 
        label: 'Yes, I have experienced nausea and WOULD like to explore the option of including an additive which may help reduce your nausea',
        value: 'nausea',
        hasOptionalAdditive: true,
        additiveLabel: 'Optional: Select an option to help with nausea',
        additives: [
          { label: 'B6 (Pyridoxine) - Additive', value: 'b6' },
          { label: 'Zofran® (Ondansetron 4mg) - Doctor Prescribed', value: 'zofran-prescribed' },
          { label: 'No additive', value: 'none' }
        ]
      },
      { 
        label: 'Yes, I have experienced muscle loss and WOULD like to explore the option of including an additive which may help to support muscle health',
        value: 'muscle-wasting',
        hasOptionalAdditive: true,
        additiveLabel: 'Optional: Select an additive to support muscle health',
        additives: [
          { label: 'Glycine', value: 'glycine' },
          { label: 'L-Carnitine', value: 'l-carnitine' },
          { label: 'No additive', value: 'none' }
        ]
      },
      { label: 'No', value: 'none' }
    ]
  },
  
  // Treatment Continuation — labels are verbatim from Drive
  // 1-xr4JPRP9fh2EMzK6PvFxwbFL-9myBAo0IRtaEdgM-4 (Updated 1/8/2026).
  treatmentContinuation: {
    id: 'treatment-continuation',
    type: 'single-select',
    question: 'How would you like to continue your treatment?',
    skipIfRestartConsent: true,
    options: [
      { label: 'Stay at the same dose or equivalent dose', value: 'same-dose' },
      { label: "Increase the dose if a higher one is available, or continue with my current dose if it's already at the maximum", value: 'increase-dose' },
      { label: 'Decrease dose', value: 'decrease-dose' },
      { label: 'I would like to change to a different medication', value: 'change-medication' }
    ]
  },
  
  // Doctor Questions
  doctorQuestions: {
    id: 'doctor-questions',
    type: 'textarea',
    question: 'What other information or questions do you have for the doctor?',
    placeholder: 'Type your questions or concerns here...',
    required: false
  },
  
  // Truthfulness Consent
  truthfulnessConsent: {
    id: 'truthfulness-consent',
    type: 'consent-dropdown',
    question: 'Consent (Truthfulness)',
    consentText: `Please attest to the following confirming that all information you have provided to us is true and complete.

I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.`,
    options: [
      { label: 'I have read the above information and I do consent and wish to move forward', value: 'consent' },
      { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
    ]
  },
  
  // GLP-1 Informed Consent
  glp1Consent: {
    id: 'glp1-consent',
    type: 'consent-dropdown',
    question: 'Informed Consent for Compounded GLP-1 and GIP/GLP-1 Receptor Agonist Therapy',
    consentText: `You are requesting treatment with a compounded GLP-1 receptor agonist or GIP/GLP-1 receptor agonist medication, such as semaglutide, tirzepatide, or liraglutide, as part of a medical weight management or obesity treatment plan. These medications work by mimicking incretin hormones that regulate appetite, slow gastric emptying, promote fullness, and improve metabolic health.

Compounded medications are custom-prepared by licensed pharmacists. These formulations may allow for alternative dosing schedules, titration strategies, dosing frequencies, routes of administration, or the addition of ingredients intended to improve tolerability or reduce side effects. Compounded medications are not reviewed or approved by the U.S. Food and Drug Administration (FDA) and are not subject to the same testing, clinical trials, or manufacturing oversight as FDA-approved brand-name products. As a result, compounded medications may differ from FDA-approved products in consistency, potency, efficacy, and safety.

Weight loss medications are typically prescribed for individuals who are obese, or for individuals who are overweight with related medical conditions. Use of these medications in individuals who are overweight without an associated medical condition is considered off-label, meaning the medication is prescribed in a manner not specifically approved by the FDA. Off-label prescribing is a common and accepted medical practice when clinically appropriate and based on medical judgment.

Potential benefits of treatment may include weight loss or weight management, improved blood glucose control, reduced cardiovascular risk, and overall improvement in metabolic health. While many patients tolerate these medications well, they may cause side effects and, in rare cases, serious adverse events including emergency room visits, hospitalization, or death. Common side effects include nausea, vomiting, diarrhea, constipation, decreased appetite, and indigestion. Serious or less common risks include pancreatitis, low blood sugar, gallbladder disease, kidney injury or worsening kidney function, allergic reactions, and gastrointestinal motility disorders such as gastroparesis.

These medications are not safe for use during pregnancy or while breastfeeding, as they may pose risks to a developing baby. The FDA advises the use of additional contraception because these medications may reduce the effectiveness of oral contraceptives. If you use oral birth control, you must continue it with a barrier method, such as condoms, for the first month after starting treatment and for one month after any dose increase. You may choose to switch to a non-oral contraceptive method, such as an intrauterine device or implant, prior to starting treatment. After stopping the medication, you should continue using effective contraception for at least two months to ensure the medication has fully cleared from your system before attempting to conceive.

Ongoing follow-up is required to monitor your response to treatment and to assess for side effects. Medication doses may be adjusted based on tolerability and clinical response.

By proceeding with treatment, you acknowledge that compounded medications are not FDA-approved and may differ from FDA-approved products in safety and effectiveness. You understand the potential benefits, risks, side effects, and pregnancy-related precautions associated with GLP-1 and GIP/GLP-1 receptor agonist therapy, including possible off-label use, and agree to follow prescribed instructions, attend required follow-up, and promptly report any concerns or side effects.`,
    options: [
      { label: 'I have read and understand the above information and I wish to continue', value: 'consent' },
      { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
    ]
  },
  
  // Medication Selection - after GLP-1 consent
  medicationSelection: {
    id: 'medication-selection',
    type: 'medication-selection',
    question: 'What core ingredient would you like your provider to consider?',
    options: [
      { label: 'Tirzepatide', value: 'tirzepatide', description: 'Mounjaro, Zepbound' },
      { label: 'Semaglutide', value: 'semaglutide', description: 'Ozempic, Wegovy' }
    ]
  },
  
  // Checkout Step - Review treatment and pay
  checkout: {
    id: 'checkout',
    type: 'checkout',
    question: 'Review treatment and pay'
  }
};

// ============================================
// FOLLOW-UP FLOW CONFIGURATION
// ============================================
export const followUpFlow = [
  'medical-conditions',
  'self-reported-meds',
  'allergies',
  'sex-at-birth',
  'pregnancy-status', // Conditional: Female only
  'height-weight',
  'health-changes',
  'health-changes-details', // Conditional: health-changes === 'yes'
  'last-medication-date',
  'dose-decrease', // Conditional: last-medication-date === 'within-2-3-weeks'
  'restart-medication', // Conditional: last-medication-date === 'over-a-month'
  'last-use-date',
  'current-dose',
  'medication-details',
  'side-effects',
  'side-effects-details', // Conditional: side-effects === 'yes'
  'side-effects-additives', // Conditional: side-effects === 'yes'
  'treatment-continuation',
  'doctor-questions',
  'truthfulness-consent',
  'glp1-consent',
  'medication-selection',
  'checkout'
];

// Get question by ID
export const getQuestionById = (id) => {
  return Object.values(followUpQuestions).find(q => q.id === id);
};

// Build dynamic flow based on answers
export const buildDynamicFollowUpFlow = (answers, userData) => {
  const flow = [];
  let skipToQuestionId = null;
  
  // Check if restart medication consent was given
  const hasRestartConsent = answers['restart-medication'] === 'consent';
  
  // Check if patient is on starter dose (for restart medication logic)
  const patientOnStarterDose = userData?.patientPreference ? isOnStarterDose(userData.patientPreference) : false;
  
  // For restart consent:
  // - If on starter dose (Semaglutide 0.2mg or Tirzepatide 1.5mg) -> Skip to doctor questions
  // - If on higher dose -> Show all remaining questions (last use date, current dose, etc.)
  const shouldSkipRestartQuestions = hasRestartConsent && patientOnStarterDose;
  
  for (const questionId of followUpFlow) {
    const question = getQuestionById(questionId);
    if (!question) continue;
    
    // Handle skipToQuestion - if we're skipping to a specific question, skip until we reach it
    if (skipToQuestionId) {
      if (questionId === skipToQuestionId) {
        skipToQuestionId = null; // Found target, stop skipping
      } else {
        continue; // Keep skipping
      }
    }
    
    // Check skipIfRestartConsent - only skip if patient is on STARTER dose
    // If patient is on higher dose, they need to complete all questions
    if (question.skipIfRestartConsent && shouldSkipRestartQuestions) {
      continue; // Skip this question only for restart patients on starter dose
    }
    
    // Check showIf conditions
    if (question.showIf) {
      const { field, value } = question.showIf;
      if (answers[field] !== value) {
        continue; // Skip this question
      }
    }
    
    // Check skipIf conditions
    if (question.skipIf) {
      const { field, value } = question.skipIf;
      if (answers[field] === value) {
        continue; // Skip this question
      }
    }
    
    // Special handling for sex-at-birth based pregnancy skip
    if (questionId === 'pregnancy-status') {
      const sex = answers['sex-at-birth'] || userData?.sex;
      if (sex === 'Male') {
        continue; // Skip pregnancy question for males
      }
    }
    
    // Check if this question's answer triggers a skipToQuestion
    // Only apply skipToQuestion for starter dose patients
    if (question.options) {
      const selectedValue = answers[question.id];
      const selectedOption = question.options.find(opt => opt.value === selectedValue);
      if (selectedOption?.skipToQuestion && patientOnStarterDose) {
        skipToQuestionId = selectedOption.skipToQuestion;
      }
    }
    
    flow.push(question);
  }
  
  return flow;
};

export default followUpQuestions;
